import { useCurrentUser } from '@klp/auth';
import type { Aktivitet } from '@klp/formgenerator/interfaces';
import { getConfig } from '@klpkonsern/appconfig';

export const useFormAuth = () => {
    const { currentUser } = useCurrentUser();
    const schemaSpecificRoles = getConfig('WEBAPP_APP_CAMUNDA_AUTH_WHITELIST')
        ?.split(',')
        .map((s) => s.trim());

    // Helper functions
    const matchUserToCandidateGroups = (candidateGroups: Array<string>) => {
        if (!currentUser) return false;

        candidateGroups = candidateGroups.filter(
            (group) => !schemaSpecificRoles?.includes(group),
        );
        if (candidateGroups.length === 0) return true;

        return currentUser.roller.some((userRole) =>
            candidateGroups.includes(userRole.rolle),
        );
    };

    const isActivityCompleted = (activity: Aktivitet) => {
        return activity.id !== null && activity.utfortDato !== null;
    };

    const isActivityAssignedToCurrentUser = (activity: Aktivitet) => {
        if (!currentUser) return false;

        return activity.tildeltTil === currentUser.epost;
    };

    // Exported functions
    const hasCurrentUserSubmitPrivileges = (
        kandidatGruppeEier: Aktivitet['kandidatGruppeEier'],
    ) => {
        if (currentUser?.isEDAdmin) return true;

        return matchCurrentUserToCandidateGroup(kandidatGruppeEier);
    };

    const matchCurrentUserToCandidateGroup = (
        kandidatGruppe: Aktivitet['kandidatGruppe'],
    ) => {
        const candidateGroupArray = kandidatGruppe
            .split(',')
            .map((r) => r.trim());

        return matchUserToCandidateGroups(candidateGroupArray);
    };

    const isActivityEditable = (
        activity: Aktivitet,
        checkUserRoles: boolean = true,
        checkUserAssigned: boolean = true,
    ) => {
        if (isActivityCompleted(activity)) return false;

        if (currentUser?.isEDAdmin) return true;

        const validUserRole =
            !checkUserRoles ||
            matchCurrentUserToCandidateGroup(activity.kandidatGruppe);
        const validAssigned =
            !checkUserAssigned || isActivityAssignedToCurrentUser(activity);

        return validUserRole && validAssigned;
    };

    return {
        isActivityEditable,
        hasCurrentUserSubmitPrivileges,
        matchCurrentUserToCandidateGroup,
    };
};
